import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import {
  getPublicCreatorChallenge,
  DonateChallenge,
} from '../../api/payment/donate-challenge.api';
import {
  clockSkewMs,
  countdownLabel,
  formatCa,
  needsRefresh,
  overlayView,
  raceProgress,
} from '../../utils/donate-challenge';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';

/**
 * Overlay THỬ THÁCH DONATE — chèn vào OBS bằng Browser Source.
 *
 * Chạy KHÔNG đăng nhập: lấy thử thách qua endpoint công khai, và nghe cùng hub
 * SignalR mà overlay thông báo donate đang dùng (/payment-hubs?userid=...).
 * Đếm ngược dựa trên serverNow của máy chủ để không lệch khi đồng hồ máy sai
 * giờ; hết giờ mà chưa thấy chốt xong thì tự tải lại mỗi 20 giây (job chốt
 * chạy mỗi phút) cho tới khi máy chủ báo Finalized.
 */
const OUTLINE =
  '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';

const FINALIZE_POLL_MS = 20_000;

const PageDonateChallengeLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  const barColor = searchParams.get('color') || '39539E';
  const textColor = searchParams.get('textColor') || 'FFFFFF';

  const [challenge, setChallenge] = useState<DonateChallenge | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const skewRef = useRef(0);
  // Để load() bỏ qua response cũ nếu contentCreatorId đã đổi trước khi request
  // trả về (route overlay có :contentCreatorId nên về lý thuyết có thể đổi).
  const contentCreatorIdRef = useRef(contentCreatorId);
  useEffect(() => {
    contentCreatorIdRef.current = contentCreatorId;
  }, [contentCreatorId]);

  const load = useCallback(async () => {
    if (!contentCreatorId) return;
    const requestedId = contentCreatorId;
    try {
      const data = await getPublicCreatorChallenge(requestedId);
      // contentCreatorId đã đổi trong lúc chờ response -> dữ liệu này không còn
      // thuộc về overlay đang hiển thị, bỏ qua.
      if (contentCreatorIdRef.current !== requestedId) return;
      skewRef.current = clockSkewMs(data.serverNow);
      setChallenge(data);
    } catch {
      // Lỗi mạng: giữ nguyên đang hiện, lần donate/tick sau sẽ tải lại.
    }
  }, [contentCreatorId]);

  useEffect(() => {
    load();
  }, [load]);

  // Đồng hồ 1 giây, đã cộng độ lệch với máy chủ để đếm ngược không sai khi
  // máy người xem chỉnh sai giờ.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now() + skewRef.current), 1000);
    return () => clearInterval(t);
  }, []);

  // Đã qua mốc mà máy chủ chưa báo trạng thái mới -> tải lại mỗi 20 giây để bắt kịp: Running đã
  // hết giờ (chờ job chốt chạy mỗi phút) HOẶC Scheduled đã qua giờ bắt đầu (chờ chuyển Running) —
  // thiếu vế Scheduled từng làm overlay đứng yên ở "Bắt đầu sau Đã hết giờ". `needsPoll` chỉ đổi
  // giá trị khi thật sự qua mốc hoặc khi status đổi — KHÔNG đổi mỗi giây như `now`, nên
  // usePollWhile không bị dọn-tạo-lại interval liên tục (bug cũ: effect cũ liệt kê thẳng `now`
  // vào deps nên interval 20 giây không bao giờ sống đủ lâu để bắn).
  const needsPoll = needsRefresh(challenge, now);
  usePollWhile(needsPoll, load, FINALIZE_POLL_MS);

  useEffect(() => {
    if (!contentCreatorId) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection
      .start()
      .then(() => {
        // Có donate mới -> lấy lại số đúng từ máy chủ (BE cache 5 giây là đủ).
        connection.on('ReceiveDonationInfo', () => load());
      })
      .catch(() => {
        // Không nối được hub: vẫn hiện thử thách ở lần tải đầu.
      });

    return () => {
      connection.stop();
    };
  }, [contentCreatorId, load]);

  const view = overlayView(challenge, now);

  // Chưa có thử thách / đã huỷ / hết cửa sổ quán quân -> không vẽ gì để OBS
  // trong suốt hoàn toàn.
  if (view === 'hidden' || !challenge) return null;

  const textStyle = { color: `#${textColor}`, textShadow: OUTLINE };

  return (
    <div className="flex h-screen w-full items-start justify-center bg-transparent p-4">
      <div className="w-full max-w-[560px]">
        <div className="mb-2 truncate text-3xl font-extrabold" style={textStyle}>
          {challenge.title}
        </div>

        {view === 'scheduled' && (
          <>
            <div className="text-2xl font-extrabold" style={textStyle}>
              {countdownLabel('Scheduled', new Date(challenge.startAt).getTime() - now)}
            </div>
            {challenge.rewardText && (
              <div className="mt-1 text-xl font-semibold" style={textStyle}>
                {challenge.rewardText}
              </div>
            )}
          </>
        )}

        {view === 'running' && (
          <>
            <div className="text-2xl font-extrabold" style={textStyle}>
              {countdownLabel('Running', new Date(challenge.endAt).getTime() - now)}
            </div>
            {challenge.rewardText && (
              <div className="mt-1 text-xl font-semibold" style={textStyle}>
                {challenge.rewardText}
              </div>
            )}

            <div className="mt-3 flex flex-col gap-2">
              {challenge.mode === 'Top' &&
                challenge.leaderboard.slice(0, 5).map((entry) => (
                  <div
                    key={entry.rank}
                    className="flex items-baseline justify-between gap-2 text-xl font-extrabold"
                    style={textStyle}
                  >
                    <span className="min-w-0 truncate">
                      #{entry.rank} {entry.isAnonymous ? 'Người ẩn danh' : entry.displayName}
                    </span>
                    <span className="shrink-0">{formatCa(entry.amount)} Ca</span>
                  </div>
                ))}

              {challenge.mode === 'Race' &&
                challenge.leaderboard.slice(0, 5).map((entry) => {
                  const percent = raceProgress(entry.amount, challenge.targetAmount);
                  return (
                    <div key={entry.rank}>
                      <div
                        className="flex items-baseline justify-between gap-2 text-xl font-extrabold"
                        style={textStyle}
                      >
                        <span className="min-w-0 truncate">
                          #{entry.rank} {entry.isAnonymous ? 'Người ẩn danh' : entry.displayName}
                          {entry.reachedAt && ' · Về đích'}
                        </span>
                        <span className="shrink-0">{formatCa(entry.amount)} Ca</span>
                      </div>
                      <div className="mt-1 h-6 w-full overflow-hidden rounded-full bg-black/40 ring-2 ring-black/30">
                        <div
                          className="h-full rounded-full transition-[width] duration-700 ease-out"
                          style={{ width: `${percent}%`, backgroundColor: `#${barColor}` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}

        {view === 'champion' && (
          <>
            <div className="text-2xl font-extrabold" style={textStyle}>
              Quán quân
            </div>
            <div className="mt-2 flex flex-col gap-2">
              {challenge.winners.length === 0 && (
                <div className="text-xl font-semibold" style={textStyle}>
                  Chưa có ai đạt thử thách
                </div>
              )}
              {challenge.winners.map((w) => (
                <div
                  key={w.rank}
                  className="flex items-baseline justify-between gap-2 text-xl font-extrabold"
                  style={textStyle}
                >
                  <span className="min-w-0 truncate">
                    #{w.rank} {w.isAnonymous ? 'Người ẩn danh' : w.displayName}
                  </span>
                  <span className="shrink-0">{formatCa(w.amount)} Ca</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PageDonateChallengeLivestream;
